
import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  ClientsIcon,
  CreditNoteIcon,
  DocumentIcon,
  HomeIcon,
  MenuIcon,
  ProductsIcon,
  QuoteIcon,
  SettingsIcon,
  UsersIcon,
  UserIcon,
  NoteIcon,
  TaskIcon,
  TrendingDownIcon,
} from "./Icons";
import { Shield, ShieldCheck, Building2, FileText, ChevronDown, LayoutDashboard, Receipt, Briefcase, ClipboardList, Lock, Cog } from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { AvisoCertificado } from "./AvisoCertificado";
import { useUser } from "@/contexts/UserContext";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavGroup {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    id: "principal",
    label: "Principal",
    icon: LayoutDashboard,
    items: [
      { to: "/", label: "Dashboard", icon: HomeIcon },
    ],
  },
  {
    id: "facturacion",
    label: "Facturación",
    icon: Receipt,
    items: [
      { to: "/facturas", label: "Facturas", icon: DocumentIcon },
      { to: "/presupuestos", label: "Presupuestos", icon: QuoteIcon },
      { to: "/proformas", label: "Proformas", icon: FileText },
      { to: "/rectificativas", label: "Facturas Rect.", icon: CreditNoteIcon },
    ],
  },
  {
    id: "gestion",
    label: "Gestión",
    icon: Briefcase,
    items: [
      { to: "/clientes", label: "Clientes", icon: ClientsIcon },
      { to: "/productos", label: "Productos", icon: ProductsIcon },
      { to: "/gastos", label: "Gastos", icon: TrendingDownIcon },
    ],
  },
  {
    id: "productividad",
    label: "Productividad",
    icon: ClipboardList,
    items: [
      { to: "/tareas", label: "Tareas", icon: TaskIcon },
      { to: "/notas", label: "Notas", icon: NoteIcon },
    ],
  },
  {
    id: "cumplimiento",
    label: "Cumplimiento",
    icon: Lock,
    items: [
      { to: "/certificados", label: "Certificados", icon: Shield },
      { to: "/verifactu", label: "Verifactu", icon: ShieldCheck },
      { to: "/face", label: "FACe", icon: Building2 },
    ],
  },
  {
    id: "sistema",
    label: "Sistema",
    icon: Cog,
    items: [
      { to: "/usuarios", label: "Usuarios", icon: UsersIcon },
      { to: "/configuracion", label: "Configuración", icon: SettingsIcon },
    ],
  },
];

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    principal: true,
    facturacion: true,
    gestion: true,
    productividad: false,
    cumplimiento: false,
    sistema: false,
  });
  const { userProfile, logout } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleGroup = (groupId: string) => {
    setOpenGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const isGroupActive = (group: NavGroup) => {
    return group.items.some(item => location.pathname === item.to ||
      (item.to !== "/" && location.pathname.startsWith(item.to)));
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error("Failed to log out", error);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 left-0 z-40 w-72 transform border-r bg-card shadow-lg transition-transform duration-300 md:translate-x-0 flex flex-col ${
            open ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between p-4 border-b">
            <Link to="/" className="text-xl font-extrabold tracking-tight">
              <span className="text-primary">Factura</span>
              <span className="text-accent">PRO</span>
            </Link>
            <button className="md:hidden" onClick={() => setOpen(false)} aria-label="Cerrar menú">
              ✕
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto p-3 space-y-1">
            {navGroups.map((group) => (
              <Collapsible
                key={group.id}
                open={openGroups[group.id]}
                onOpenChange={() => toggleGroup(group.id)}
              >
                <CollapsibleTrigger className={cn(
                  "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent",
                  isGroupActive(group) && "text-primary"
                )}>
                  <div className="flex items-center gap-2">
                    <group.icon className="h-4 w-4" />
                    <span>{group.label}</span>
                  </div>
                  <ChevronDown className={cn(
                    "h-4 w-4 transition-transform duration-200",
                    openGroups[group.id] && "rotate-180"
                  )} />
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 mt-1 space-y-0.5">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent",
                          isActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground"
                        )
                      }
                      onClick={() => setOpen(false)}
                    >
                      <item.icon className="h-4 w-4" />
                      {item.label}
                    </NavLink>
                  ))}
                </CollapsibleContent>
              </Collapsible>
            ))}
          </nav>
          <div className="p-4 border-t">
            <div className="rounded-md bg-muted/50 p-3 text-sm">
              <p className="font-semibold">ERP para autónomos</p>
              <p className="text-muted-foreground text-xs mt-1">Cumple FacturaE y normativa española.</p>
            </div>
          </div>
        </aside>

        {/* Overlay */}
        {open && (
          <div
            className="fixed inset-0 z-30 bg-black/40 md:hidden"
            onClick={() => setOpen(false)}
            aria-hidden
          />
        )}

        {/* Main Content Area */}
        <div className="flex min-h-screen flex-1 flex-col md:ml-72">
          <header className="sticky top-0 z-20 flex items-center justify-between border-b bg-background/80 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex items-center gap-3">
              <button className="md:hidden" aria-label="Abrir menú" onClick={() => setOpen(true)}>
                <MenuIcon />
              </button>
              <span className="hidden text-sm text-muted-foreground md:block">
                FacturaPRO — ERP para Freelancers
              </span>
            </div>
            <div className="flex items-center gap-4">
              <ThemeToggle />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <UserIcon />
                    <span>{userProfile?.nombre || "Mi Cuenta"}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/configuracion">Configuración</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <AvisoCertificado />

          <main className="flex-grow p-4 md:p-8">{children}</main>
          
          <footer className="p-4 text-center text-xs text-muted-foreground">
            FacturaPro2 es un ERP para Freelances diseñado por Raúl Alonso y desarrollado por el Instituto San Miguel de Computación e Inteligencia Artificial. © 2025 · Todos los derechos reservados.
          </footer>
        </div>
      </div>
    </div>
  );
};
